/**
 * @module lib/categories
 * @summary Library category helpers (built-in + user-added).
 * @description Manages category add/delete/rename/reorder/active-toggle and
 *   per-symbol overrides (move to category, hide built-in symbol).
 */
import { KEYS, lsGet, lsSet } from './storage';
import { registry } from '../engine/shapes/registry';
import { BUILTIN_CATEGORY_ORDER } from '../engine/shapes/defs';
import { apiGetCatConfig, apiSaveCatConfig, getApiCreds } from './api';
import type { CatConfig } from '../types';

// ─── Shared arrangement sync ──────────────────────────────────────────────────

/** Snapshot the full category arrangement from localStorage. */
function getLocalConfig(): CatConfig {
  return {
    custom: getCustomCats(),
    order: getCatOrder(),
    renames: getCatRenames(),
    inactive: getInactiveCats(),
    moves: getCatMoves(),
    deleted: getDeletedSymbols(),
  };
}

/**
 * Push the FULL arrangement to the Worker so every user shares the same order,
 * names, visibility and per-symbol placement. Fire-and-forget; needs manager+
 * credentials (engineers read but don't write).
 */
function pushConfig(): void {
  const creds = getApiCreds();
  if (creds) apiSaveCatConfig(getLocalConfig(), creds).catch(() => {});
}

// ─── Custom categories ────────────────────────────────────────────────────────

export function getCustomCats(): string[] {
  return lsGet<string[]>(KEYS.customCats, []);
}
function saveCustomCats(arr: string[]): void {
  lsSet(KEYS.customCats, arr);
  pushConfig();
}

/**
 * Pull the shared arrangement from the Worker and apply it locally so the
 * library looks identical in every browser.
 *
 * - Custom category NAMES are merged (union) so a brand-new local category is
 *   never lost to a stale server response.
 * - Arrangement fields (order, renames, visibility, moves, deletions) are
 *   GLOBAL: the server copy wins when it has data, otherwise we keep local and
 *   seed the server (handles first-time migration from the old array format).
 */
export async function syncCatsFromApi(): Promise<void> {
  const local = getLocalConfig();
  const remote = await apiGetCatConfig();

  if (!remote) {
    // No server config yet — seed it from local so others inherit it.
    const creds = getApiCreds();
    if (creds && (local.custom.length || local.order.length)) {
      apiSaveCatConfig(local, creds).catch(() => {});
    }
    return;
  }

  const custom = [...remote.custom];
  for (const c of local.custom) if (!custom.includes(c)) custom.push(c);

  const merged: CatConfig = {
    custom,
    order: remote.order?.length ? remote.order : local.order,
    renames: Object.keys(remote.renames || {}).length ? remote.renames : local.renames,
    inactive: remote.inactive?.length ? remote.inactive : local.inactive,
    moves: Object.keys(remote.moves || {}).length ? remote.moves : local.moves,
    deleted: remote.deleted?.length ? remote.deleted : local.deleted,
  };

  lsSet(KEYS.customCats, merged.custom);
  lsSet(KEYS.catOrder, merged.order);
  lsSet(KEYS.catRenames, merged.renames);
  lsSet(KEYS.inactiveCats, merged.inactive);
  lsSet(KEYS.catMoves, merged.moves);
  lsSet(KEYS.deletedSymbols, merged.deleted);

  // If our merged view adds anything the server lacks, push it back to converge.
  const creds = getApiCreds();
  if (creds && JSON.stringify(merged) !== JSON.stringify(remote)) {
    apiSaveCatConfig(merged, creds).catch(() => {});
  }
}

// ─── Category renames ─────────────────────────────────────────────────────────

export function getCatRenames(): Record<string, string> {
  return lsGet<Record<string, string>>(KEYS.catRenames, {});
}
function saveCatRenames(r: Record<string, string>): void {
  lsSet(KEYS.catRenames, r);
  pushConfig();
}

/** Get the display name for a category (returns renamed name if set). */
export function getDisplayName(cat: string): string {
  return getCatRenames()[cat] ?? cat;
}

/** Rename a category. Returns error string or null on success. */
export function renameCat(original: string, newName: string): string | null {
  const n = newName.trim();
  if (!n) return 'Name is required.';
  const allCats = getAllCategories();
  const renames = getCatRenames();
  // Check for collision with any existing display name (excluding self)
  for (const c of allCats) {
    if (c !== original && (renames[c] ?? c) === n) return `"${n}" is already in use.`;
  }
  renames[original] = n;
  saveCatRenames(renames);
  return null;
}

// ─── Inactive categories ──────────────────────────────────────────────────────

export function getInactiveCats(): string[] {
  return lsGet<string[]>(KEYS.inactiveCats, []);
}
function saveInactiveCats(arr: string[]): void {
  lsSet(KEYS.inactiveCats, arr);
  pushConfig();
}
export function isCatActive(cat: string): boolean {
  return !getInactiveCats().includes(cat);
}
export function toggleCatActive(cat: string): void {
  const inactive = getInactiveCats();
  if (inactive.includes(cat)) {
    saveInactiveCats(inactive.filter((c) => c !== cat));
  } else {
    saveInactiveCats([...inactive, cat]);
  }
}

// ─── Category order ───────────────────────────────────────────────────────────

export function getCatOrder(): string[] {
  return lsGet<string[]>(KEYS.catOrder, []);
}
function saveCatOrder(order: string[]): void {
  lsSet(KEYS.catOrder, order);
  pushConfig();
}

/** Move a category one position earlier in the ordered list. */
export function moveCatUp(cat: string, allCats: string[]): void {
  const order = allCats.slice();
  const idx = order.indexOf(cat);
  if (idx <= 0) return;
  [order[idx - 1], order[idx]] = [order[idx], order[idx - 1]];
  saveCatOrder(order);
}

/** Move a category one position later in the ordered list. */
export function moveCatDown(cat: string, allCats: string[]): void {
  const order = allCats.slice();
  const idx = order.indexOf(cat);
  if (idx < 0 || idx >= order.length - 1) return;
  [order[idx], order[idx + 1]] = [order[idx + 1], order[idx]];
  saveCatOrder(order);
}

// ─── Deleted symbols (hide built-ins from the library) ───────────────────────

export function getDeletedSymbols(): string[] {
  return lsGet<string[]>(KEYS.deletedSymbols, []);
}
export function deleteBuiltinSymbol(type: string): void {
  const deleted = getDeletedSymbols();
  if (!deleted.includes(type)) {
    lsSet(KEYS.deletedSymbols, [...deleted, type]);
    pushConfig();
  }
}
export function isSymbolHidden(type: string): boolean {
  return getDeletedSymbols().includes(type);
}

// ─── Category moves (reassign a symbol to a different category) ───────────────

export function getCatMoves(): Record<string, string> {
  return lsGet<Record<string, string>>(KEYS.catMoves, {});
}
export function moveSymbolToCat(type: string, cat: string): void {
  const moves = getCatMoves();
  moves[type] = cat;
  lsSet(KEYS.catMoves, moves);
  pushConfig();
}
export function getEffectiveCat(type: string, originalCat: string): string {
  return getCatMoves()[type] ?? originalCat;
}

// ─── Main getAllCategories ────────────────────────────────────────────────────

/** All categories (original names): built-in + custom, respecting user-saved order. */
export function getAllCategories(): string[] {
  const out: string[] = [];
  for (const def of Object.values(registry.allDefs())) {
    if (!out.includes(def.g)) out.push(def.g);
  }
  for (const c of BUILTIN_CATEGORY_ORDER) {
    if (!out.includes(c)) out.push(c);
  }
  for (const c of getCustomCats()) {
    if (!out.includes(c)) out.push(c);
  }
  // Apply user custom order.
  const saved = getCatOrder();
  if (saved.length > 0) {
    const known = new Set(out);
    const remaining = out.filter((c) => !saved.includes(c));
    return [...saved.filter((c) => known.has(c)), ...remaining];
  }
  return out;
}

/** Whether a category is user-added (vs built-in). */
export function isCustomCat(cat: string): boolean {
  return getCustomCats().includes(cat);
}

/** Add a custom category. Returns an error message, or null on success. */
export function addCustomCat(name: string): string | null {
  const n = name.trim();
  if (!n) return 'Category name is required.';
  if (getAllCategories().map((c) => (getCatRenames()[c] ?? c).toLowerCase()).includes(n.toLowerCase()))
    return `"${n}" already exists.`;
  saveCustomCats([...getCustomCats(), n]);
  return null;
}

/** Delete a custom category (built-ins cannot be deleted). */
export function deleteCustomCat(name: string): void {
  saveCustomCats(getCustomCats().filter((c) => c !== name));
  // Remove from saved order too.
  saveCatOrder(getCatOrder().filter((c) => c !== name));
}
